import { useCallback, useEffect, useState } from "react";
import { API, fileUrl } from "@/lib/api";
import { isEmbedUrl } from "@/components/layers";

const ASSETS = "ibs-assets-v1";
const REG_KEY = "ibs-offline-books";

// ---- Service worker registration ----
export const registerSW = () => {
  if (!("serviceWorker" in navigator)) return;
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
};

// ---- Install prompt (Add to Home Screen) ----
let deferredPrompt = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    window.dispatchEvent(new Event("ibs-installable"));
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    window.dispatchEvent(new Event("ibs-installed"));
  });
}

export const useInstallPrompt = () => {
  const standalone = typeof window !== "undefined" && (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone);
  const [canInstall, setCanInstall] = useState(!!deferredPrompt);
  useEffect(() => {
    const on = () => setCanInstall(!!deferredPrompt);
    const off = () => setCanInstall(false);
    window.addEventListener("ibs-installable", on);
    window.addEventListener("ibs-installed", off);
    return () => { window.removeEventListener("ibs-installable", on); window.removeEventListener("ibs-installed", off); };
  }, []);
  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return false;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    deferredPrompt = null;
    setCanInstall(false);
    return outcome === "accepted";
  }, []);
  return { canInstall: canInstall && !standalone, promptInstall };
};

// ---- Online status ----
export const useOnline = () => {
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true), down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, []);
  return online;
};

// ---- Offline book registry (localStorage) ----
const readReg = () => { try { return JSON.parse(localStorage.getItem(REG_KEY) || "{}"); } catch { return {}; } };
const writeReg = (r) => localStorage.setItem(REG_KEY, JSON.stringify(r));
export const isBookSaved = (slug) => !!readReg()[slug];
export const savedBooks = () => readReg();

// All cacheable asset URLs for a book manifest. Only same-origin files we host
// (pages + uploaded audio/video/images) can be cached — cross-origin embeds
// (YouTube/Vimeo or external links) are skipped as browsers can't cache them.
export const bookAssetUrls = (manifest) => {
  const urls = new Set();
  const add = (u) => { if (u && u.includes("/api/files/")) urls.add(u); };
  (manifest.pages || []).forEach((p) => ["sm", "lg", "thumb"].forEach((k) => p[k] && add(fileUrl(p[k]))));
  Object.values(manifest.layers || {}).forEach((arr) => (arr || []).forEach((l) => {
    [l.src, l.poster, l.icon].forEach((s) => { if (s && !isEmbedUrl(s)) add(fileUrl(s)); });
  }));
  return [...urls];
};

const stripKey = (u) => { try { const url = new URL(u, window.location.origin); return url.origin + url.pathname; } catch { return u; } };

// Download every self-hosted asset + public API JSON into the shared cache for offline reading.
export const saveBookOffline = async (slug, title, manifest, onProgress) => {
  if (!("caches" in window)) throw new Error("Offline storage is not supported on this device");
  const cache = await caches.open(ASSETS);
  const apiUrls = [`${API}/public/books/${slug}`, `${API}/public/books/${slug}/lines`, `${API}/public/books/${slug}/text`];
  const assetUrls = bookAssetUrls(manifest);
  const total = apiUrls.length + assetUrls.length;
  let done = 0;
  const tick = () => { done += 1; onProgress?.(Math.min(100, Math.round((done / Math.max(1, total)) * 100))); };

  for (const u of apiUrls) {
    try { const r = await fetch(u, { credentials: "include" }); if (r.ok) await cache.put(u, r.clone()); } catch { /* skip */ }
    tick();
  }
  const queue = [...assetUrls];
  const worker = async () => {
    while (queue.length) {
      const key = stripKey(queue.shift());
      try { if (!(await cache.match(key))) { const r = await fetch(key); if (r.ok) await cache.put(key, r.clone()); } } catch { /* skip */ }
      tick();
    }
  };
  await Promise.all([worker(), worker(), worker(), worker()].map((p) => p.catch(() => {})));

  const reg = readReg();
  reg[slug] = { title, savedAt: Date.now(), urls: [...apiUrls.map(stripKey), ...assetUrls.map(stripKey)] };
  writeReg(reg);
};

export const removeBookOffline = async (slug) => {
  const reg = readReg();
  const entry = reg[slug];
  if (entry && "caches" in window) {
    const cache = await caches.open(ASSETS);
    await Promise.all((entry.urls || []).map((u) => cache.delete(u).catch(() => {})));
  }
  delete reg[slug];
  writeReg(reg);
};
